import { Boundaries } from "@planzone/core";
import { project, projectTimeSheet, TimeSheet, timeSheet } from "@planzone/dto";
import { Event, events } from "@planzone/events";
import { setRequestStateToPending } from "../../utils/setRequestStateToPending";
import { setRequestStateToSuccess } from "../../utils/setRequestStateToSuccess";
import { setRequestStateToFailure } from "../../utils/setRequestStateToFailure";
import { createStateReducer } from "../../utils/createStateReducer";
import { reduceList } from "../../common/list";
import { reduceResource } from "../../common/resource";
import { State, Selector } from "./types";
import { createSelector } from "./selector";
import { matchRequest, mergeTS } from "./utils";

const initialState: State = [];

function handleRequest(
  state: State,
  {
    project,
    filter,
    size,
    page,
  }: Boundaries["retrieveProjectTimeSheet"]["request"],
): State {
  const index = state.findIndex((req) => matchRequest(req, project, filter));

  return index !== -1
    ? [
        ...state.slice(0, index),
        {
          ...state[index],
          project,
          filter,
          request: setRequestStateToPending(),
        },
        ...state.slice(index + 1),
      ]
    : [
        ...state,
        {
          project,
          filter,
          request: setRequestStateToPending(),
          timeSheet: projectTimeSheet,
          size,
          page,
          canLoadMore: true,
          isDirty: false,
        },
      ];
}

function handleSuccess(
  state: State,
  {
    project,
    filter,
    timeSheet,
    size,
    page,
  }: Boundaries["retrieveProjectTimeSheet"]["success"],
): State {
  const index = state.findIndex((req) => matchRequest(req, project, filter));

  return index !== -1
    ? [
        ...state.slice(0, index),
        {
          ...state[index],
          project,
          filter,
          request: setRequestStateToSuccess(),
          timeSheet:
            page === 0 && !state[index].isDirty
              ? timeSheet
              : {
                  ...state[index].timeSheet,
                  ...timeSheet,
                  resources: [
                    ...state[index].timeSheet.resources.slice(0, page * size),
                    ...timeSheet.resources,
                    ...state[index].timeSheet.resources.slice(
                      page * size + size,
                    ),
                  ],
                },
          canLoadMore: state[index].isDirty
            ? state[index].canLoadMore
            : timeSheet.resources.length >= size,
          page: page + 1,
          isDirty: false,
        },
        ...state.slice(index + 1),
      ]
    : [
        ...state,
        {
          project,
          filter,
          request: setRequestStateToSuccess(),
          timeSheet: projectTimeSheet,
          size,
          page,
          canLoadMore: timeSheet.resources.length >= size,
          isDirty: false,
        },
      ];
}

function handleFailure(
  state: State,
  {
    project,
    filter,
    size,
    page,
  }: Boundaries["retrieveProjectTimeSheet"]["failure"],
): State {
  const index = state.findIndex((req) => matchRequest(req, project, filter));

  return index !== -1
    ? [
        ...state.slice(0, index),
        {
          ...state[index],
          project,
          filter,
          request: setRequestStateToFailure(),
        },
        ...state.slice(index + 1),
      ]
    : [
        ...state,
        {
          project,
          filter,
          request: setRequestStateToSuccess(),
          timeSheet: projectTimeSheet,
          size,
          page,
          canLoadMore: true,
          isDirty: false,
        },
      ];
}

function handleAllocationResources(state: State): State {
  return state.map((req) => ({ ...req, isDirty: true }));
}

function handleEditAllocation(state: State): State {
  return state.map((req) => ({ ...req, isDirty: true }));
}

function handleRemoveAllocation(state: State): State {
  return state.map((req) => ({ ...req, isDirty: true }));
}

function handleValidateTimeRecordsUpToDate(state: State): State {
  return state.map((req) => ({ ...req, isDirty: true }));
}

function handleDisableProjectResource(state: State): State {
  return state.map((req) => ({ ...req, isDirty: true }));
}

function handleActivateProjectResource(state: State): State {
  return state.map((req) => ({ ...req, isDirty: true }));
}

function handleRemoveResourceFromProject(state: State): State {
  return state.map((req) => ({ ...req, isDirty: true }));
}

function handleRemoveResourceFromPlanzone(state: State): State {
  return state.map((req) => ({ ...req, isDirty: true }));
}

function handleEditTimesheetResource(state: State): State {
  return state.map((req) => ({ ...req, isDirty: true }));
}

function handleRemoveTask(state: State): State {
  return state.map((req) => ({ ...req, isDirty: true }));
}

function handleRemoveList(state: State): State {
  return state.map((req) => ({ ...req, isDirty: true }));
}

function handleReload(
  state: State,
  {
    project,
    filter,
    timeSheet,
  }: Boundaries["reloadProjectTimeSheet"]["success"],
): State {
  const index = state.findIndex((req) => matchRequest(req, project, filter));

  return index !== -1
    ? [
        ...state.slice(0, index),
        {
          ...state[index],
          // timeSheet: mergeTS(state[index].timeSheet, timeSheet),
        },
        ...state.slice(index + 1),
      ]
    : state;
}

function propagateEvent(state = initialState, event: Event): State {
  return state.map((req) => ({
    ...req,
    timeSheet: {
      ...req.timeSheet,
      resources: req.timeSheet.resources.map((entry) => ({
        ...entry,
        ...reduceResource(entry, event),
      })),
    },
  }));
}

const reduceState = createStateReducer(
  initialState,
  {
    [events.retrieveProjectTimeSheetRequested]: handleRequest,
    [events.retrieveProjectTimeSheetSucceeded]: handleSuccess,
    [events.retrieveProjectTimeSheetFailed]: handleFailure,
    [events.allocateResourcesSucceeded]: handleAllocationResources,
    [events.editAllocationSucceeded]: handleEditAllocation,
    [events.removeAllocationSucceeded]: handleRemoveAllocation,
    [events.validateTimeRecordsUpToDateSucceeded]:
      handleValidateTimeRecordsUpToDate,
    [events.reloadProjectTimeSheetSucceeded]: handleReload,
    [events.disableProjectResourceSucceeded]: handleDisableProjectResource,
    [events.activateProjectResourceSucceeded]: handleActivateProjectResource,
    [events.removeResourceFromProjectSucceeded]:
      handleRemoveResourceFromProject,
    [events.removeResourceFromPlanzoneSucceeded]:
      handleRemoveResourceFromPlanzone,
    [events.saveTimeRecordSucceeded]: handleEditTimesheetResource,
    [events.deleteTimeRecordSucceeded]: handleEditTimesheetResource,
    [events.removeTaskSucceeded]: handleRemoveTask,
    [events.removeListSucceeded]: handleRemoveList,
  },
  propagateEvent,
);

export type { State, Selector };

export { reduceState, initialState, createSelector };
